import {
  ActionIcon,
  Badge,
  Group,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import { IconArrowDown, IconArrowUp, IconGripVertical, IconTrash } from "@tabler/icons-react";
import type { ContentBlock } from "../../types/blogTypes";

interface SectionListProps {
  sections: ContentBlock[];
  onRemove: (index: number) => void;
  onMove: (index: number, direction: "up" | "down") => void;
}

export function SectionList({ sections, onRemove, onMove }: SectionListProps) {
  return (
    <Paper withBorder radius="md" p="lg">
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Title order={3}>Sections ({sections.length})</Title>
        </Group>

        {sections.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="md">
            No sections yet. Add one from the left panel.
          </Text>
        ) : (
          <Stack gap="sm">
            {sections.map((section, index) => (
              <Paper
                key={`${section.type}-${index}`}
                p="sm"
                radius="sm"
                withBorder
                style={{
                  backgroundColor: "var(--oculera-surface-alt)",
                  cursor: "grab",
                }}
              >
                <Group justify="space-between" align="center" gap="xs">
                  <Group gap="xs" align="center" style={{ flex: 1 }}>
                    <ThemeIcon variant="subtle" size="sm" color="gray">
                      <IconGripVertical size={14} />
                    </ThemeIcon>
                    <Stack gap={0} style={{ flex: 1 }}>
                      <Badge size="sm">{section.type}</Badge>
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {section.type === "paragraph" && section.text?.substring(0, 50)}
                        {section.type === "title" && section.text}
                        {section.type === "numbered" && `${section.items?.length || 0} items`}
                        {section.type === "references" && `${section.items?.length || 0} refs`}
                      </Text>
                    </Stack>
                  </Group>
                  <Group gap={4}>
                    {index > 0 && (
                      <Tooltip label="Move up">
                        <ActionIcon variant="subtle" size="sm" onClick={() => onMove(index, "up")}>
                          <IconArrowUp size={14} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                    {index < sections.length - 1 && (
                      <Tooltip label="Move down">
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          onClick={() => onMove(index, "down")}
                        >
                          <IconArrowDown size={14} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                    <Tooltip label="Delete">
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size="sm"
                        onClick={() => onRemove(index)}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Group>
              </Paper>
            ))}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
